// @ts-strict-ignore
import uniq from 'lodash/uniq';
import produce from 'immer';

import {Translatable} from 'common/i18n';
import {Timezone} from 'common/constants';
import {RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {
  AnalysisType,
  ChartPlotStyle,
  ChartType,
  InsightsBookmarkParams,
  MetricType,
  PRIMITIVE_MATH_TYPES,
  ValueRepresentationType,
} from 'common/types/reports/bookmark';
import {getTimeSection} from 'common/report/insights/util/time';
import {
  DisplayOptions,
  InsightsBookmarkSections,
  InsightsQueryResults,
  ShowClause as ShowClauseType,
} from 'common/report/insights/util/types';
import {generateCohortBaselineOptions} from 'common/report/util/lift';
import {LiftType, SegmentLiftBaselineOption} from 'common/report/util/lift/types';
import {
  getInvalidChartTypesForInsightsBookmark,
  getRestrictionReasonForTimeUnit,
  getSupportedTimeUnits,
} from 'common/report/insights/util';
import {defaultRollingWindowSize, getOwnAttr} from 'common/util';
import {ChartHeaderUpdateDetail} from 'common/widgets/chart-header';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {isCohortSegment, Segment} from 'common/query-builder/query-block/segment-menu/types';
import {comparisonDateToBookmarkFormat} from 'common/query-builder/query-block/time-comparison-menu/utils';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {TimePeriodSettings} from 'common/report/stores/types';
import {ChartCustomizationUpdate} from 'common/query-builder/chart-customization/axis/types';

import {TIME_COMPARISON_DISABLED_REASONS} from '../models/constants';
import {Report} from '../models/report';
import {getKeysAtDepth} from '../util';
import {isBehaviorShowClause, isRetentionOnlyInsightsBookmark} from '../models/utils';
import {DEFAULT_STEPS_TABLE_COLUMNS} from './constants';
import {isTimeSeriesChartType} from './charts/chart-display/utils';

const NON_LINE_CHART_TYPES = new Set([ChartType.Bar, ChartType.Pie, ChartType.Table, ChartType.Metric]);
const FUNNEL_SPECIFIC_CHART_TYPES = new Set([ChartType.FunnelSteps, ChartType.FunnelTopPaths]);
const NON_QUERYABLE_TYPE_CHANGES = new Set([AnalysisType.Linear, AnalysisType.Logarithmic]);

export function getSelectedChartType(report: Report): ChartType {
  return toCombinedChartType(report.displayOptions.chartType, report.displayOptions.plotStyle);
}

export function getSubDayGranularityDisabledMessage(report: Report): string {
  if (report.sections.show.hasAnyOrderFunnelsQuery()) {
    return `Sub-day time granularity is not available with any-order funnels.`;
  }
  return ``;
}

export function getTimeComparisonDisabledReason(report: Report, {displayOptionsOverrides = {}} = {}): string {
  const displayOptions = {...report.displayOptions, ...displayOptionsOverrides};
  if (displayOptions.plotStyle === ChartPlotStyle.Stacked && displayOptions.chartType !== ChartType.Bar) {
    return TIME_COMPARISON_DISABLED_REASONS.STACKED_CHART;
  }

  if (displayOptions.chartType === ChartType.Pie) {
    return TIME_COMPARISON_DISABLED_REASONS.PIE_CHART;
  }
  if (displayOptions.chartType === ChartType.FunnelTopPaths) {
    return TIME_COMPARISON_DISABLED_REASONS.TOP_PATHS;
  }
  if (displayOptions.chartType === ChartType.RetentionCurve) {
    return TIME_COMPARISON_DISABLED_REASONS.RETENTION_CURVE;
  }
  return null;
}

export function getTimeComparisonDisabledReasonMessage(report: Report, reason): string {
  switch (reason) {
    case TIME_COMPARISON_DISABLED_REASONS.PEOPLE_QUERY: {
      return `Time comparison is not available for profile queries`;
    }
    case TIME_COMPARISON_DISABLED_REASONS.STACKED_CHART: {
      const chartType = report.displayOptions.chartType;
      return `Time comparison is not available for stacked ${chartType} chart`;
    }
    case TIME_COMPARISON_DISABLED_REASONS.PIE_CHART: {
      return `Time comparison is not available for pie chart`;
    }
    case TIME_COMPARISON_DISABLED_REASONS.TOP_PATHS: {
      return `Time comparison is not available for Top Paths chart`;
    }
    case TIME_COMPARISON_DISABLED_REASONS.RETENTION_CURVE: {
      return `Time comparison is not available for Retention Curve`;
    }
    default:
      return ``;
  }
}

export function getTimeUnitSectionOverrides(
  report: Report,
  projectTimezone: Timezone,
  projectAsOfDate?: string,
  timePeriodSettings?: TimePeriodSettings,
): {
  [key: string]: {isDisabled: boolean; tooltip: {text: Translatable; placement: string}};
} {
  const disabledUnits = {};
  for (const unit of Object.values(TimeUnit)) {
    const reason = getRestrictionReasonForTimeUnit(
      report.serialize(),
      unit,
      projectTimezone,
      projectAsOfDate,
      timePeriodSettings,
    );
    if (reason) {
      disabledUnits[unit] = {
        isDisabled: true,
        tooltip: {
          text: reason,
          placement: `left`,
        },
      };
    }
  }
  return disabledUnits;
}

export function toCombinedChartType(chartType, plotStyle): ChartType {
  if (plotStyle === ChartPlotStyle.Stacked) {
    if (chartType === ChartType.Line) {
      return ChartType.StackedLine;
    } else if (chartType === ChartType.Bar) {
      return ChartType.StackedBar;
    } else if (chartType === ChartType.Column) {
      return ChartType.StackedColumn;
    }
  }

  return chartType;
}

function isAnalysisEnabled(displayOptions: DisplayOptions, analysisType: AnalysisType): boolean {
  let supportedAnalyses: AnalysisType[];

  switch (displayOptions.chartType) {
    case ChartType.Column:
    case ChartType.Line:
      supportedAnalyses = Object.values(AnalysisType);
      break;
    case ChartType.Bar:
      supportedAnalyses = [AnalysisType.Linear, AnalysisType.Logarithmic];
      break;
    default:
      supportedAnalyses = [AnalysisType.Linear];
      break;
  }

  return supportedAnalyses.includes(analysisType);
}

function isValueToggleEnabled(displayOptions: DisplayOptions): boolean {
  switch (displayOptions.chartType) {
    case ChartType.Bar:
    case ChartType.Table:
      return true;
    case ChartType.Line:
      return displayOptions.plotStyle === ChartPlotStyle.Stacked;
    default:
      return false;
  }
}

export function correctDisplayOptions(options): DisplayOptions {
  const displayOptions = {
    ...options,
    analysis: isAnalysisEnabled(options, options.analysis) ? options.analysis : AnalysisType.Linear,
    value: isValueToggleEnabled(options) ? options.value : ValueRepresentationType.Absolute,
  };

  if (displayOptions.analysis !== AnalysisType.Rolling) {
    delete displayOptions.rollingWindowSize;
  }

  return displayOptions;
}

export function bookmarkUpdatesFromHeaderUpdate({
  report,
  update,
  hasChangedTimeUnit,
  projectTimezone,
  projectAsOfDate,
  timePeriodSettings,
}: {
  report: Report;
  update: ChartHeaderUpdateDetail;
  hasChangedTimeUnit: boolean;
  projectTimezone: Timezone;
  projectAsOfDate?: string;
  timePeriodSettings?: TimePeriodSettings;
}): Partial<InsightsBookmarkParams> {
  const bookmarkUpdates: Partial<InsightsBookmarkParams> = {};
  bookmarkUpdates.sections = {
    ...(report.sections.toBookmarkData() as InsightsBookmarkSections),
  };

  const oldTimeUnit = report.sections.time.clauses[0].unit;
  const changedTimeUnit = getOwnAttr(update.delta, `dateUnit`);

  if (update.delta.dateRange || changedTimeUnit) {
    const modelSections = report.sections.replaceSection(
      getTimeSection(update.dateRange, {
        selectedTimeUnit: report.sections.time.clauses[0].unit,
        overrideTimeUnit: changedTimeUnit,
        // GROSS statefulness
        // use default time unit only when the user has not
        // interacted with the time unit dropdown before
        useDefaultTimeUnit: !hasChangedTimeUnit,
      }),
    );

    const bookmarkSections: InsightsBookmarkSections = modelSections.toBookmarkData();
    const newTimeClause = bookmarkSections.time[0];

    // reset time granularity for invalid time units
    const supportedTimeUnits = getSupportedTimeUnits(
      {...report.serialize(), sections: bookmarkSections},
      projectTimezone,
      projectAsOfDate,
      timePeriodSettings,
    );
    if (!supportedTimeUnits.includes(newTimeClause.unit)) {
      newTimeClause.unit = supportedTimeUnits[0];
    }

    bookmarkUpdates.sections = bookmarkSections;
  }

  if (`comparisonDateRange` in update.delta) {
    if (update.delta.comparisonDateRange) {
      bookmarkUpdates.timeComparison = comparisonDateToBookmarkFormat(update.delta.comparisonDateRange);
    } else {
      bookmarkUpdates.timeComparison = null;
    }
  }

  if (`liftComparison` in update.delta) {
    bookmarkUpdates.liftComparison = update.delta.liftComparison;
  }

  if (update.delta.chartType) {
    let chartType = update.delta.chartType;
    let plotStyle = ChartPlotStyle.Standard;
    let funnelStepsSelectedTableColumns: DisplayOptions[`funnelStepsSelectedTableColumns`] = undefined;

    const prevChartType = report.displayOptions.chartType;
    if (prevChartType === ChartType.RetentionCurve && chartType !== ChartType.RetentionCurve) {
      // reset the legend selections when changing from retention curve
      bookmarkUpdates.legend = {overrides: {}};
    }
    if (chartType === ChartType.StackedColumn) {
      chartType = ChartType.Column;
      plotStyle = ChartPlotStyle.Stacked;
    } else if (chartType === ChartType.StackedLine) {
      chartType = ChartType.Line;
      plotStyle = ChartPlotStyle.Stacked;
    } else if (chartType === ChartType.StackedBar) {
      chartType = ChartType.Bar;
      plotStyle = ChartPlotStyle.Stacked;
    } else if (chartType === ChartType.FunnelSteps) {
      funnelStepsSelectedTableColumns = {...DEFAULT_STEPS_TABLE_COLUMNS};
      const showClauses = [...report.sections.show.clauses];
      // For all funnel show clauses, change stepIndex to null
      bookmarkUpdates.sections.show = showClauses.map((showClause) => {
        if (showClause instanceof BehaviorShowClause && showClause.behavior.type === MetricType.Funnel) {
          return {
            ...showClause,
            measurement: {
              ...showClause.measurement,
              stepIndex: null,
            },
          };
        }
        return showClause;
      });
    } else if (chartType === ChartType.RetentionCurve) {
      const showClauses = [...report.sections.show.clauses];
      // For all retention show clauses, change retentionUnit to "Day" so that their units match
      bookmarkUpdates.sections.show = showClauses.map((showClause) => {
        if (showClause instanceof BehaviorShowClause && showClause.behavior.type === MetricType.Retention) {
          return {
            ...showClause,
            behavior: {
              ...showClause.behavior,
              retentionUnit: TimeUnit.Day,
            },
          };
        }
        return showClause;
      });
    }

    const isOvertimeChart = [ChartType.Column, ChartType.Line, ChartType.RetentionCurve].includes(chartType);
    const showClauses = [...report.sections.show.clauses];
    bookmarkUpdates.sections.show = showClauses.map((showClause) => {
      if (showClause instanceof BehaviorShowClause && showClause.behavior.type === MetricType.Retention) {
        return {
          ...showClause,
          behavior: {
            ...showClause.behavior,
            disableCohortize: !isOvertimeChart,
          },
        };
      }
      return showClause;
    });

    const liftType = report.liftComparison?.type;
    // we don't support stacked line/column or bar for baseline & compare to past lift, so disable if we switch to those
    // but compare to overall is fine with these stacked charts
    if (
      liftType !== LiftType.Overall &&
      plotStyle === ChartPlotStyle.Stacked &&
      [ChartType.Line, ChartType.Bar, ChartType.Column].includes(chartType)
    ) {
      report.liftComparison = null;
    }

    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...report.displayOptions,
      chartType,
      plotStyle,
      funnelStepsSelectedTableColumns,
    });

    if (chartType === ChartType.FunnelTopPaths) {
      bookmarkUpdates.sections = {
        ...(report.sections.toBookmarkData() as InsightsBookmarkSections),
        group: [],
      };
    }
  }

  if (update.delta.valueRepresentation) {
    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...report.displayOptions,
      value: update.delta.valueRepresentation,
    });
  }

  // Reset default rollingWindowSize when changing time unit
  if (changedTimeUnit && oldTimeUnit !== changedTimeUnit) {
    const showClauses = [...report.sections.show.clauses];
    bookmarkUpdates.sections.show = showClauses.map((showClause): ShowClauseType => {
      if (showClause instanceof BehaviorShowClause) {
        if (
          showClause.behavior.retentionUnit === oldTimeUnit &&
          [TimeUnit.Day, TimeUnit.Week, TimeUnit.Month].includes(changedTimeUnit) &&
          !showClause.behavior.retentionCustomBucketSizes?.length
        ) {
          showClause.behavior.retentionUnit = changedTimeUnit;
        }
        return {
          ...showClause,
          behavior: {
            ...showClause.behavior,
          },
          measurement: {
            ...showClause.measurement,
            ...(showClause.measurement.rolling && {
              rolling: {
                rollingWindowSize: defaultRollingWindowSize(changedTimeUnit),
              },
            }),
          },
        };
      }
      return {
        ...showClause,
        measurement: {
          ...showClause.measurement,
          ...(showClause.measurement.rolling && {
            rolling: {
              rollingWindowSize: defaultRollingWindowSize(changedTimeUnit),
            },
          }),
        },
      };
    });
  }
  if (report.displayOptions.analysis === AnalysisType.Rolling && changedTimeUnit && oldTimeUnit !== changedTimeUnit) {
    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...report.displayOptions,
      rollingWindowSize: defaultRollingWindowSize(changedTimeUnit),
    });
  }
  // always set display options as fallback
  bookmarkUpdates.displayOptions = correctDisplayOptions({
    ...report.displayOptions,
    ...(bookmarkUpdates.displayOptions ? bookmarkUpdates.displayOptions : {}),
  });
  return updateRevenueOffsets(bookmarkUpdates);
}

export function bookmarkUpdatesFromChartCustomization({
  originalBookmarkParams,
  update,
}: {
  originalBookmarkParams: InsightsBookmarkParams;
  update: Partial<ChartCustomizationUpdate>;
}): Partial<InsightsBookmarkParams> {
  const bookmarkUpdates: Partial<InsightsBookmarkParams> = {};
  bookmarkUpdates.sections = {
    ...originalBookmarkParams.sections,
  };

  if (update.newShowClauses) {
    bookmarkUpdates.sections.show = update.newShowClauses;
  }

  if (update.primaryYAxis) {
    bookmarkUpdates.displayOptions = correctDisplayOptions({
      ...originalBookmarkParams.displayOptions,
      ...(update.primaryYAxis ? {primaryYAxisOptions: update.primaryYAxis} : {}),
      ...(update.secondaryYAxis ? {secondaryYAxisOptions: update.secondaryYAxis} : {}),
      ...(update.xAxis ? {xAxisOptions: update.xAxis} : {}),
    });
  }

  return {...originalBookmarkParams, ...bookmarkUpdates};
}

export function shouldRequeryDisplayOptions(
  oldDisplayOptions: DisplayOptions,
  newDisplayOptions: DisplayOptions,
): boolean {
  const chartType = newDisplayOptions.chartType;
  const oldChartType = oldDisplayOptions.chartType;
  const oldAnalysis = oldDisplayOptions.analysis;
  const analysis = newDisplayOptions.analysis;

  const isTimeSeriesChart = chartType === ChartType.Line || chartType === ChartType.Column;
  const wasTimeSeriesChart = oldChartType === ChartType.Line || oldChartType === ChartType.Column;

  const isChangingToTimeSeriesChart = isTimeSeriesChart && NON_LINE_CHART_TYPES.has(oldChartType);
  const isChangingFromTimeSeriesChart = NON_LINE_CHART_TYPES.has(chartType) && wasTimeSeriesChart;
  const isChangingToFunnelCharts = FUNNEL_SPECIFIC_CHART_TYPES.has(chartType) && oldChartType !== chartType;
  const isChangingFromFunnelCharts = FUNNEL_SPECIFIC_CHART_TYPES.has(oldChartType) && oldChartType !== chartType;
  const isChangingToRetentionCharts = chartType === ChartType.RetentionCurve && oldChartType !== chartType;
  const isChangingFromRetentionCharts = oldChartType === ChartType.RetentionCurve && oldChartType !== chartType;

  // only case where we don't need to re-query is changing between from line to logarithmic view
  const isChangingFromLineToLog =
    NON_QUERYABLE_TYPE_CHANGES.has(analysis) && NON_QUERYABLE_TYPE_CHANGES.has(oldAnalysis);

  const isChangingRollingWindowSize = newDisplayOptions.rollingWindowSize !== oldDisplayOptions.rollingWindowSize;

  return (
    isChangingToTimeSeriesChart ||
    isChangingFromTimeSeriesChart ||
    isChangingToFunnelCharts ||
    isChangingFromFunnelCharts ||
    isChangingToRetentionCharts ||
    isChangingFromRetentionCharts ||
    isChangingFromLineToLog === false ||
    isChangingRollingWindowSize
  );
}

// lift

export function getLeafLevelSegments({
  breakdowns,
  queryResults,
  report,
}: {
  breakdowns: Segment[];
  queryResults: InsightsQueryResults;
  report: Report;
}): SegmentLiftBaselineOption[] {
  if (queryResults) {
    let searchDepth = 0;
    if (breakdowns.length) {
      const lastBreakdown = breakdowns[breakdowns.length - 1];
      if (isCohortSegment(lastBreakdown)) {
        return generateCohortBaselineOptions(lastBreakdown);
      }
      searchDepth = breakdowns.length;
      if (
        isRetentionOnlyInsightsBookmark(report.toBookmarkData()) &&
        report.displayOptions.chartType === ChartType.RetentionCurve
      ) {
        searchDepth += 1;
      }
    } else if (
      report.sections.show
        .getBehaviorClauses()
        .some((clause) => clause.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM)
    ) {
      // assuming the segments built from distribution are always right after the event, or 1 level deep
      // further breakdowns come after (but we would have hit the previous condition anyway)
      searchDepth = 1;
    }

    if (searchDepth) {
      return uniq(getKeysAtDepth(queryResults.series, searchDepth))
        .filter(
          // we don't support comparison to "All users" cohort yet, and we hide the "$overall" segment
          (segmentName) => segmentName !== TOP_PEOPLE.label && !segmentName.includes(RESULTS_OVERALL_SUM_KEY),
        )
        .map((segmentName: string) => {
          return {propertyValue: segmentName, displayName: segmentName};
        });
    }
  }

  return [];
}

export function getChartTypeSectionOverrides({
  bookmarkParams,
  metricsResource,
  behaviorsResource,
}: {
  bookmarkParams: InsightsBookmarkParams;
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
}) {
  const invalidChartTypes = getInvalidChartTypesForInsightsBookmark({
    bookmarkParams,
    metricsResource,
    behaviorsResource,
  });
  const chartTypeOverrides = invalidChartTypes.reduce(
    (acc, {type, reason}) => {
      acc[type] = {
        isDisabled: true,
        tooltip: {text: reason},
      };
      return acc;
    },
    {} as Record<ChartType, any>,
  );

  if (
    bookmarkParams.sections.show.length === 1 &&
    isBehaviorShowClause(bookmarkParams.sections.show[0]) &&
    bookmarkParams.sections.show[0].behavior.type === MetricType.RetentionFrequency
  ) {
    chartTypeOverrides[ChartType.RetentionCurve] = {label: ({t}) => t(`reports.chart-types.frequency-curve`)};
  }

  return chartTypeOverrides;
}

function updateRevenueOffsets(bookmarkParams: Partial<InsightsBookmarkParams>): Partial<InsightsBookmarkParams> {
  const timeUnit = bookmarkParams.sections.time?.[0]?.unit;
  const chartType = bookmarkParams.displayOptions?.chartType;
  const isIntervalChart = !isTimeSeriesChartType(chartType);
  return produce(bookmarkParams, (draft: Partial<InsightsBookmarkParams>) => {
    draft.sections.show.forEach((showClause) => {
      if (isBehaviorShowClause(showClause) && showClause.measurement.property?.behavior?.offset) {
        if (isIntervalChart) {
          showClause.measurement.property.behavior.offset.value = `$start`;
        } else {
          showClause.measurement.property.behavior.offset.value =
            showClause.measurement.property.behavior.offset.value === `$start`
              ? -1
              : showClause.measurement.property.behavior.offset.value;
          showClause.measurement.property.behavior.offset.unit = timeUnit;
        }
      }
      if (isBehaviorShowClause(showClause) && showClause.measurement.property?.scdOffset) {
        if (isIntervalChart) {
          showClause.measurement.property.scdOffset.value = `$start`;
        } else {
          showClause.measurement.property.scdOffset.value =
            showClause.measurement.property.scdOffset.value === `$start`
              ? -1
              : showClause.measurement.property.scdOffset.value;
          showClause.measurement.property.scdOffset.unit = timeUnit;
        }
      }
      if (
        isBehaviorShowClause(showClause) &&
        Array.isArray(showClause.behavior.filters) &&
        showClause.behavior.filters.length > 0
      ) {
        showClause.behavior.filters.forEach((filter) => {
          if (filter.behavior?.offset) {
            if (isIntervalChart) {
              filter.behavior.offset.value = `$start`;
            } else {
              filter.behavior.offset.value =
                filter.behavior.offset.value === `$start` ? -1 : filter.behavior.offset.value;
              filter.behavior.offset.unit = timeUnit;
            }
          }
        });
      }
    });
    draft.sections.filter.forEach((filterClause) => {
      if (filterClause.behavior?.offset) {
        if (isIntervalChart) {
          filterClause.behavior.offset.value = `$start`;
        } else {
          filterClause.behavior.offset.value =
            filterClause.behavior.offset.value === `$start` ? -1 : filterClause.behavior.offset.value;
          filterClause.behavior.offset.unit = timeUnit;
        }
      }
    });
    draft.sections.group.forEach((groupClause) => {
      if (groupClause.behavior?.offset) {
        if (isIntervalChart) {
          groupClause.behavior.offset.value = `$start`;
        } else {
          groupClause.behavior.offset.value =
            groupClause.behavior.offset.value === `$start` ? -1 : groupClause.behavior.offset.value;
          groupClause.behavior.offset.unit = timeUnit;
        }
      }
    });
  });
}
